/**
 * 栃木県の背景ツアー「栃木めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - あしかがフラワーパークは民間企業が運営する有料施設。園名・ロゴ・園内の建築・演出照明は描かない。
 *   藤棚と花房だけにする。天然記念物の指定区分と指定年は公式で確認できなかったので書かない。
 * - 華厳滝のエレベーターは民間企業の施設なので、建物や入口を画角に入れない。
 * - 明智平ロープウェイも民間施設なので、ゴンドラや駅舎を描かない。
 * - 湯西川温泉は「平家の里」の茅葺き民家の意匠を避け、河川敷のミニかまくらだけにする。数は書かない。
 * - 栃木市の蔵の街は昭和前期の建築を含むので、特定の建物を写さず町並みとして描く。看板・屋号も入れない。
 * - 渡良瀬遊水地は旧谷中村の跡地の扱いが公式で確認できなかったので、ヨシ原と谷中湖だけを描く。
 * - 足利学校には孔子を祀る孔子廟がある。学校門と築地塀だけを描く。創建は複数の説があるので断定しない。
 * - 日光東照宮と二荒山神社、神橋は鳥居と礼拝の場が避けられないので入れていない。
 * - 殺生石、大谷寺の磨崖仏も祈りの場なので入れていない。
 */

import type { StageTour } from '../types';

export const tochigiSceneryTour: StageTour = {
  scenes: [
    {
      id: 'ashikaga-fuji',
      name: '足利のフジ',
      area: '足利市',
      image: '/assets/scenery/tochigi/scenes/01_ashikaga_fuji.webp',
      hotspots: [
        {
          id: 'ashikaga-fuji-trees',
          x: 0.28,
          y: 0.3,
          title: '樹齢100年から130年',
          body: '指定の対象はノダナガフジ3本、八重黒龍1本、白フジのトンネル一式。現在の樹齢は100年から130年と推定されている。',
          source:
            'https://www.city.ashikaga.tochigi.jp/education/000029/000169/000627/p001205.html',
          mapQuery: '足利のフジ',
        },
        {
          id: 'ashikaga-fuji-length',
          x: 0.55,
          y: 0.6,
          title: '花房は最長1.7メートル',
          body: 'いずれも紫色の花をつけ、花房の長さが一番長いものは1.7メートルもある。',
          source:
            'https://www.city.ashikaga.tochigi.jp/education/000029/000169/000627/p001205.html',
          mapQuery: '足利市 大藤',
        },
        {
          id: 'ashikaga-fuji-yae',
          x: 0.78,
          y: 0.78,
          title: '八重咲きの濃い紫',
          body: 'そのうち1本は、この地方では珍しい八重咲きのフジで、ほかよりも濃い紫色をしている。見頃は4月下旬から5月上旬。',
          source:
            'https://www.city.ashikaga.tochigi.jp/education/000029/000169/000627/p001205.html',
          mapQuery: '足利市 藤',
        },
      ],
    },
    {
      id: 'watarase',
      name: '渡良瀬遊水地',
      area: '栃木市',
      image: '/assets/scenery/tochigi/scenes/02_watarase.webp',
      hotspots: [
        {
          id: 'watarase-ramsar',
          x: 0.28,
          y: 0.35,
          title: '2012年にラムサール条約湿地',
          body: '平成24年7月にラムサール条約湿地に登録された。登録湿地の面積は2,861ヘクタール。',
          source: 'https://www.city.tochigi.lg.jp/soshiki/6/1370.html',
          mapQuery: '渡良瀬遊水地',
        },
        {
          id: 'watarase-four',
          x: 0.55,
          y: 0.6,
          title: '四つの県にまたがる',
          body: '茨城県、群馬県、埼玉県および栃木県の4県にまたがっている。',
          source: 'https://www.pref.tochigi.lg.jp/d04/eco/shizenkankyou/shizen/1250055408591.html',
          mapQuery: '渡良瀬遊水地 谷中湖',
        },
        {
          id: 'watarase-reeds',
          x: 0.8,
          y: 0.78,
          title: '本州最大のヨシ原',
          body: '栃木市は「本州最大のヨシ原を主体をする湿生草地」と記している。希少種約60種を含む約1,000種の豊富な植物が確認されている。',
          source: 'https://www.city.tochigi.lg.jp/soshiki/6/1370.html',
          mapQuery: '渡良瀬遊水地 ヨシ原',
        },
      ],
    },
    {
      id: 'kirifuri',
      name: '霧降高原',
      area: '日光市',
      image: '/assets/scenery/tochigi/scenes/03_kirifuri.webp',
      hotspots: [
        {
          id: 'kirifuri-altitude',
          x: 0.28,
          y: 0.35,
          title: '標高約1200メートルの高原',
          body: '赤薙山の斜面に広がる標高約1,200メートルの高原地帯。',
          source: 'https://www.nikko-kankou.org/spot/8',
          mapQuery: '霧降高原',
        },
        {
          id: 'kirifuri-lilies',
          x: 0.55,
          y: 0.62,
          title: '約26万株',
          body: 'ニッコウキスゲの群生地として知られ、6月下旬から7月中旬には約26万株が見ごろを迎える。',
          source: 'https://www.nikko-kankou.org/spot/8',
          mapQuery: '霧降高原 キスゲ平園地',
        },
        {
          id: 'kirifuri-slope',
          x: 0.78,
          y: 0.82,
          title: '天空回廊へ続く階段',
          body: '園地には、スロープ状の散策路と、天空回廊に続く階段が整備されている。',
          source: 'https://www.nikko-kankou.org/spot/8',
          mapQuery: '日光市 霧降高原',
        },
      ],
    },
    {
      id: 'senjogahara',
      name: '戦場ヶ原',
      area: '日光市',
      image: '/assets/scenery/tochigi/scenes/04_senjogahara.webp',
      hotspots: [
        {
          id: 'senjogahara-ramsar',
          x: 0.28,
          y: 0.4,
          title: '2005年にラムサール条約湿地',
          body: '平成17年11月8日、ウガンダの首都カンパラで開かれた会議でラムサール条約湿地に登録された。面積は260.41ヘクタール。',
          source: 'https://www.pref.tochigi.lg.jp/d04/eco/shizenkankyou/shizen/ramsar.html',
          mapQuery: '戦場ヶ原',
        },
        {
          id: 'senjogahara-area',
          x: 0.55,
          y: 0.7,
          title: '登録されたのは四つの場所',
          body: '登録の対象は湯ノ湖、湯川、戦場ヶ原、小田代原。',
          source: 'https://www.pref.tochigi.lg.jp/d04/eco/shizenkankyou/shizen/ramsar.html',
          mapQuery: '小田代原',
        },
        {
          id: 'senjogahara-plants',
          x: 0.8,
          y: 0.3,
          title: '100種以上の湿原の植物',
          body: '戦場ヶ原にはワタスゲ、レンゲツツジ、ホザキシモツケなど100種以上の湿原性植物があり、オオジシギやノビタキなどが繁殖する。',
          source: 'https://www.pref.tochigi.lg.jp/d04/eco/shizenkankyou/shizen/ramsar.html',
          mapQuery: '戦場ヶ原 三本松',
        },
      ],
    },
    {
      id: 'irohazaka',
      name: 'いろは坂と明智平',
      area: '日光市',
      image: '/assets/scenery/tochigi/scenes/05_irohazaka.webp',
      hotspots: [
        {
          id: 'irohazaka-curves',
          x: 0.3,
          y: 0.55,
          title: '48か所の急カーブ',
          body: '下り専用の第一いろは坂と上り専用の第二いろは坂の二つの坂を合計すると、48か所もの急カーブがある。いろは48文字にたとえて名づけられた。',
          source: 'https://www.nikko-kankou.org/spot/9',
          mapQuery: 'いろは坂',
        },
        {
          id: 'irohazaka-height',
          x: 0.6,
          y: 0.75,
          title: '標高差は約440メートル',
          body: '標高差は約440メートル。カーブごとに「い」「ろ」「は」と順に看板が表示される。',
          source: 'https://www.nikko-kankou.org/spot/9',
          mapQuery: '第二いろは坂',
        },
        {
          id: 'irohazaka-view',
          x: 0.8,
          y: 0.3,
          title: '明智平からの眺め',
          body: '第二いろは坂の途中にある明智平展望台からの眺めは抜群で、第一いろは坂を望むこともできる。秋には日光で最も紅葉が美しい場所とされる。',
          source: 'https://www.nikko-kankou.org/spot/9',
          mapQuery: '明智平展望台',
        },
      ],
    },
    {
      id: 'ryuokyo',
      name: '龍王峡',
      area: '日光市',
      image: '/assets/scenery/tochigi/scenes/06_ryuokyo.webp',
      hotspots: [
        {
          id: 'ryuokyo-origin',
          x: 0.28,
          y: 0.35,
          title: '2200万年前の海底火山',
          body: '今からおよそ2,200万年前、海底火山の活動によって噴出した火山岩が鬼怒川の流れによって侵食され、現在のような景観になった。',
          source: 'https://www.nikko-kankou.org/spot/42',
          mapQuery: '龍王峡',
        },
        {
          id: 'ryuokyo-length',
          x: 0.55,
          y: 0.7,
          title: '約3キロの渓谷',
          body: '川治温泉と鬼怒川温泉のあいだ、およそ3キロメートルにおよぶ。ハイキングコースは約7キロで、3〜4時間ほどで歩ける。',
          source: 'https://www.nikko-kankou.org/spot/42',
          mapQuery: '龍王峡 日光市',
        },
        {
          id: 'ryuokyo-name',
          x: 0.8,
          y: 0.5,
          title: '名づけられたのは昭和25年',
          body: 'その光景がまるで龍がのたうつような姿であることから、昭和25年に名づけられた。',
          source: 'https://www.nikko-kankou.org/spot/42',
          mapQuery: '龍王峡駅',
        },
      ],
    },
    {
      id: 'tochigi-kura',
      name: '蔵の街 嘉右衛門町',
      area: '栃木市',
      image: '/assets/scenery/tochigi/scenes/07_kura.webp',
      hotspots: [
        {
          id: 'tochigi-kura-selected',
          x: 0.28,
          y: 0.35,
          title: '平成24年に選定',
          body: '重要伝統的建造物群保存地区としての選定は平成24年7月9日。面積は約9.6ヘクタール。',
          source: 'https://www.city.tochigi.lg.jp/soshiki/5/1568.html',
          mapQuery: '嘉右衛門町伝統的建造物群保存地区',
        },
        {
          id: 'tochigi-kura-street',
          x: 0.55,
          y: 0.62,
          title: '旧日光例幣使道に沿って',
          body: '旧日光例幣使道に沿って見世蔵や土蔵をはじめとする江戸末期から昭和前期頃にかけての伝統的な建造物が群としてよく残る。',
          source: 'https://www.city.tochigi.lg.jp/soshiki/5/1568.html',
          mapQuery: '栃木市 蔵の街',
        },
        {
          id: 'tochigi-kura-river',
          x: 0.8,
          y: 0.8,
          title: '巴波川と陣屋跡の緑',
          body: '地形に沿って湾曲する道、巴波川、翁島や陣屋跡の緑などとともに、特徴的な歴史的風致をつくり上げている。',
          source: 'https://www.city.tochigi.lg.jp/soshiki/5/1568.html',
          mapQuery: '巴波川 栃木市',
        },
      ],
    },
    {
      id: 'kegon',
      name: '華厳滝',
      area: '日光市',
      image: '/assets/scenery/tochigi/scenes/08_kegon.webp',
      hotspots: [
        {
          id: 'kegon-height',
          x: 0.5,
          y: 0.3,
          title: '高さ約97メートル',
          body: '中禅寺湖の水が、高さ約97メートルの岸壁を一気に落下する壮大な滝。',
          source: 'https://www.nikko-kankou.org/spot/5',
          mapQuery: '華厳滝',
        },
        {
          id: 'kegon-three',
          x: 0.25,
          y: 0.6,
          title: '日本三名瀑に数えられる滝',
          body: '日本三名瀑の一つにも数えられている。',
          source: 'https://www.city.nikko.lg.jp/kurashi_tetsuzuki/kankyohozen/6/1/4325.html',
          mapQuery: '華厳滝観瀑台',
        },
        {
          id: 'kegon-ice',
          x: 0.78,
          y: 0.65,
          title: '冬はブルーアイス',
          body: '1月から2月は、十二滝と呼ばれる細い小滝が凍るため、滝全体がブルーアイスに彩られる。',
          source: 'https://www.nikko-kankou.org/spot/5',
          mapQuery: '日光市 華厳滝',
        },
      ],
    },
    {
      id: 'yunishigawa',
      name: '湯西川温泉 かまくら祭',
      area: '日光市',
      image: '/assets/scenery/tochigi/scenes/09_yunishigawa.webp',
      hotspots: [
        {
          id: 'yunishigawa-venue',
          x: 0.28,
          y: 0.35,
          title: '会場は河川敷',
          body: '会場は沢口河川敷会場と平家の里で、同時に開かれる。',
          source: 'https://www.nikko-kankou.org/spot/1210',
          mapQuery: '湯西川温泉 かまくら祭',
        },
        {
          id: 'yunishigawa-time',
          x: 0.55,
          y: 0.62,
          title: '点灯は17時半から21時',
          body: '点灯の時間は17時30分から21時まで。',
          source: 'https://www.nikko-kankou.org/spot/1210',
          mapQuery: '沢口河川敷',
        },
        {
          id: 'yunishigawa-scene',
          x: 0.8,
          y: 0.8,
          title: '灯がともると',
          body: 'ミニかまくらに灯りがともされると、幻想的な夜の景色が広がる。',
          source: 'https://www.nikko-kankou.org/spot/1210',
          mapQuery: '湯西川温泉',
        },
      ],
    },
    {
      id: 'ashikaga-gakko',
      name: '史跡足利学校',
      area: '足利市',
      image: '/assets/scenery/tochigi/scenes/10_ashikaga_gakko.webp',
      hotspots: [
        {
          id: 'ashikaga-gakko-oldest',
          x: 0.28,
          y: 0.35,
          title: '「日本最古の学校」といわれる',
          body: '足利学校は「日本最古の学校」「日本最古の総合大学」などといわれています。',
          source: 'https://www.city.ashikaga.tochigi.jp/education/000031/000180/p001417.html',
          mapQuery: '史跡足利学校',
        },
        {
          id: 'ashikaga-gakko-shiseki',
          x: 0.55,
          y: 0.62,
          title: '大正10年に国の史跡',
          body: '大正10年（1921年）3月、足利学校跡は孔子廟、学校門などの建物を含めて国の指定史跡となった。',
          source: 'https://www.city.ashikaga.tochigi.jp/education/000031/000180/p001417.html',
          mapQuery: '足利学校 学校門',
        },
        {
          id: 'ashikaga-gakko-theories',
          x: 0.8,
          y: 0.8,
          title: '創建には複数の説',
          body: '創建については、奈良時代の国学の遺制とする説、平安時代初期の天長9年に小野篁が創建したとする説、鎌倉時代初期に足利義兼が建てたとする説、室町時代中期の永享11年に上杉憲実が開いたとする説がある。いまの孔子廟、学校門、杏壇門は、寛文8年（1668年）の大修築のときの建物。',
          source: 'https://www.city.ashikaga.tochigi.jp/education/000031/000180/p001417.html',
          mapQuery: '足利市 足利学校',
        },
      ],
    },
  ],
};
